// @flow
import 'react-datepicker/dist/react-datepicker.css'
import './date-input.css'

import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import * as React from 'react'
import DatePicker from 'react-datepicker'
import styled from 'styled-components'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import { type Props as TextInputProps } from './TextInput'

const DateInputLabel = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  text-align: left;
`

function DateInput({
  disabled,
  inputField,
  ...props
}: TextInputProps): React.Node {
  const [isFocused, setIsFocused] = React.useState(false)

  const dateInputRef: { current: null | { input: HTMLInputElement } } =
    React.useRef(null)

  const { answers, resourceId, setAnswers, signatureAnswers, viewMode } =
    React.useContext(PDFFillerContext)

  const linkedSignatureDate = React.useMemo(() => {
    if (viewMode === 'office-use' || !inputField.linked_signature) return null

    const answer = signatureAnswers[inputField.linked_signature]

    if (!answer || !answer.created_at) return null

    return new Date(answer.created_at)
  }, [viewMode, inputField.linked_signature, signatureAnswers])

  const handleChange = React.useCallback(
    (date: Date) => {
      const year = date?.getFullYear()
      if (year && year.toString().length > 4) return null

      const raw_value = date ? date.toISOString().split('T')[0] : null

      setAnswers((previousAnswers) => ({
        ...previousAnswers,
        [inputField.id]: {
          answer_raw: raw_value,
          input_field: inputField.id,
          response: resourceId,
        },
      }))
    },
    [inputField.id, resourceId, setAnswers]
  )

  const dateValue = React.useMemo(() => {
    const answer = answers[inputField.id]?.answer_raw
    if (!answer) return null

    const [yearStr, monthStr, dayStr] = answer.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    const day = parseInt(dayStr, 10)

    return new Date(year, month, day)
  }, [answers, inputField.id])

  const onFocus = React.useCallback(() => {
    setIsFocused(true)
  }, [])

  const onBlur = React.useCallback(() => {
    setIsFocused(false)
  }, [])

  const hasAnswer =
    isFocused || Boolean(linkedSignatureDate) || Boolean(dateValue)

  const onClickWrapper = () => {
    if (dateInputRef.current) {
      dateInputRef.current.input.focus()
    }
  }

  const CustomInput = React.forwardRef((rest, ref) => (
    <input
      data-input-field={props['data-input-field']}
      data-response-id={props['data-response-id']}
      ref={ref}
      {...rest}
    />
  ))

  return (
    <div className="pl-2 pt-1 text-left" onClick={onClickWrapper}>
      {!isFocused && !hasAnswer && (
        <DateInputLabel className="pl-2 pt-1">mm/dd/yyyy</DateInputLabel>
      )}
      {viewMode !== 'edit' && (
        <TriggerUserActions>
          <DatePicker
            className="date-input"
            customInput={<CustomInput />}
            disabled={Boolean(inputField.linked_signature) || disabled}
            onBlur={onBlur}
            onChange={handleChange}
            onFocus={onFocus}
            ref={dateInputRef}
            selected={
              inputField.linked_signature ? linkedSignatureDate : dateValue
            }
          />
        </TriggerUserActions>
      )}
    </div>
  )
}

export default DateInput
